/**
 * HubNavBar - The exact same quick-links + pills from the Hub home page.
 * Used on other pages (e.g. StockManagement) so users have the same fast-nav they know.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import {
  FileText, FileQuestion, Package, ClipboardList, TrendingUp, UserPlus,
  ChevronDown, ChevronUp, Plus, Upload, Clock, X, ShoppingCart,
} from 'lucide-react';
import Proforma from '../pages/Proforma';
import Quotes from '../pages/Quotes';
import LogCallDropdownPill from './LogCallDropdownPill';
import ToolsDropdown from './ToolsDropdown';
import { StockRequestModal } from './StockRequestModal';

const enconOrange = '#ee7203';
const pillClass = `flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 rounded-full bg-white border-2 hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap`;
const pillTextClass = `font-semibold text-[11px] lg:text-xs xl:text-sm`;
const pillIconClass = `w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4`;

export default function HubNavBar({ user }) {
  const navigate = useNavigate();
  const proformaDropdownRef = useRef(null);
  const quotesDropdownRef = useRef(null);

  const [proformaDropdownOpen, setProformaDropdownOpen] = useState(false);
  const [quotesDropdownOpen, setQuotesDropdownOpen] = useState(false);
  const [proformaModalMode, setProformaModalMode] = useState(null);
  const [quotesModalMode, setQuotesModalMode] = useState(null);
  const [showNewAccountsModal, setShowNewAccountsModal] = useState(false);
  const [showStockRequestModal, setShowStockRequestModal] = useState(false);
  const [stockRequestDropdownOpen, setStockRequestDropdownOpen] = useState(false);
  const stockRequestDropdownRef = useRef(null);

  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (proformaDropdownRef.current && !proformaDropdownRef.current.contains(e.target)) setProformaDropdownOpen(false);
      if (quotesDropdownRef.current && !quotesDropdownRef.current.contains(e.target)) setQuotesDropdownOpen(false);
      if (stockRequestDropdownRef.current && !stockRequestDropdownRef.current.contains(e.target)) setStockRequestDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [qlRes, lgRes] = await Promise.all([
          api.get(`${API}/quick-links`),
          api.get(`${API}/quick-link-groups`),
        ]);
        setQuickLinks(qlRes.data || []);
        setLinkGroups(lgRes.data || []);
      } catch { /* silent */ }
    };
    load();
  }, []);

  const getLinksForGroup = (groupId) =>
    quickLinks.filter(l => (l.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);

  const handleLinkClick = (link) => {
    if (link.url?.startsWith('http')) window.open(link.url, '_blank', 'noopener,noreferrer');
    else navigate(link.url);
  };

  return (
    <>
      {/* Quick Links text row */}
      {quickLinks.length > 0 && linkGroups.length > 0 && (
        <div className="hidden md:flex justify-center mb-4">
          <div className="flex flex-wrap justify-center items-center gap-1 px-4">
            {linkGroups.map((group, groupIndex) => {
              const groupLinks = getLinksForGroup(group.id);
              if (groupLinks.length === 0) return null;
              return (
                <span key={group.id} className="flex items-center">
                  {groupIndex > 0 && linkGroups.slice(0, groupIndex).some(g => getLinksForGroup(g.id).length > 0) && (
                    <span className="mx-3 text-gray-400 font-bold">||</span>
                  )}
                  {groupLinks.map((link, li) => (
                    <span key={link.id} className="flex items-center">
                      <button
                        onClick={() => handleLinkClick(link)}
                        className="hover:underline transition-all text-sm font-medium px-1"
                        style={{ color: group.color }}
                      >
                        {link.name}
                      </button>
                      {li < groupLinks.length - 1 && <span className="text-gray-300 mx-1">|</span>}
                    </span>
                  ))}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Pill bar — desktop only (same as Hub page) */}
      <div className="hidden md:flex justify-center mb-6 px-1 md:px-2 lg:px-4 xl:px-8">
        <div className="flex flex-wrap md:flex-nowrap justify-center items-center gap-1 lg:gap-2 xl:gap-3 w-full">

          {/* Pro Forma */}
          <div className="relative" ref={proformaDropdownRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setProformaDropdownOpen(o => !o); setQuotesDropdownOpen(false); }}
              className={`${pillClass} ${proformaDropdownOpen ? 'shadow-lg' : ''}`}
              style={{ borderColor: enconOrange }}
            >
              <FileText className={pillIconClass} style={{ color: enconOrange }} />
              <span className={pillTextClass} style={{ color: enconOrange }}>Pro Forma</span>
              <ChevronDown className={`${pillIconClass} transition-transform ${proformaDropdownOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
            </button>
            {proformaDropdownOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-1 w-48 rounded-2xl shadow-xl overflow-hidden z-50"
                style={{ background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)', border: `2px solid ${enconOrange}`, backdropFilter: 'blur(10px)', top: '100%' }}>
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{ background: 'rgba(45,45,45,0.98)', borderLeft: `2px solid ${enconOrange}`, borderTop: `2px solid ${enconOrange}` }} />
                <div className="relative">
                  <button onClick={() => { setProformaModalMode('create'); setProformaDropdownOpen(false); }} className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl">
                    <Plus className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium">Create New</span>
                  </button>
                  <button onClick={() => { setProformaModalMode('upload'); setProformaDropdownOpen(false); }} className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 rounded-b-xl">
                    <Upload className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium">Upload from PDF</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Quotes */}
          <div className="relative" ref={quotesDropdownRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setQuotesDropdownOpen(o => !o); setProformaDropdownOpen(false); }}
              className={`${pillClass} ${quotesDropdownOpen ? 'shadow-lg' : ''}`}
              style={{ borderColor: enconOrange }}
            >
              <FileQuestion className={pillIconClass} style={{ color: enconOrange }} />
              <span className={pillTextClass} style={{ color: enconOrange }}>Quotes</span>
              <ChevronDown className={`${pillIconClass} transition-transform ${quotesDropdownOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
            </button>
            {quotesDropdownOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-1 w-48 rounded-2xl shadow-xl overflow-hidden z-50"
                style={{ background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)', border: `2px solid ${enconOrange}`, backdropFilter: 'blur(10px)', top: '100%' }}>
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{ background: 'rgba(45,45,45,0.98)', borderLeft: `2px solid ${enconOrange}`, borderTop: `2px solid ${enconOrange}` }} />
                <div className="relative">
                  <button onClick={() => { setQuotesModalMode('create'); setQuotesDropdownOpen(false); }} className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl">
                    <Plus className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium">Create New</span>
                  </button>
                  <button onClick={() => { setQuotesModalMode('recent'); setQuotesDropdownOpen(false); }} className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10">
                    <Clock className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium">Recent Quotes</span>
                  </button>
                  <button onClick={() => { setQuotesModalMode('upload'); setQuotesDropdownOpen(false); }} className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 rounded-b-xl">
                    <Upload className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium">Upload PDF</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Log CRM Call */}
          <LogCallDropdownPill user={user} />

          {/* Combined Stock Transfers / Requests dropdown */}
          <div className="relative" ref={stockRequestDropdownRef}>
            <button
              data-testid="hubnav-stock-transfers-requests"
              onClick={() => setStockRequestDropdownOpen(o => !o)}
              className={`${pillClass}`}
              style={{ borderColor: enconOrange }}
            >
              <Package className={pillIconClass} style={{ color: enconOrange }} />
              <span className={pillTextClass} style={{ color: enconOrange }}>Transfers / Requests</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${stockRequestDropdownOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
            </button>
            {stockRequestDropdownOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 mt-1 w-56 rounded-2xl shadow-xl overflow-hidden z-50"
                style={{
                  background: 'linear-gradient(180deg, rgba(45,45,45,0.98) 0%, rgba(30,30,30,0.98) 100%)',
                  border: `2px solid ${enconOrange}`,
                  backdropFilter: 'blur(10px)',
                  top: '100%',
                }}
              >
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45"
                  style={{ background: 'rgba(45,45,45,0.98)', borderLeft: `2px solid ${enconOrange}`, borderTop: `2px solid ${enconOrange}` }} />
                <div className="relative">
                  <button
                    onClick={() => { setStockRequestDropdownOpen(false); navigate('/stock-transfer'); }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl"
                  >
                    <Package className="w-4 h-4" style={{ color: enconOrange }} />
                    <span className="font-medium">Stock Transfers</span>
                  </button>
                  <div className="border-t border-white/25 mx-3 my-0.5" />
                  <button
                    onClick={() => { setStockRequestDropdownOpen(false); setShowStockRequestModal(true); }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-b-xl"
                  >
                    <ShoppingCart className="w-4 h-4" style={{ color: enconOrange }} />
                    <span className="font-medium">Stock Request</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* P.O. Expenses */}
          <button onClick={() => navigate('/po-expenses')} className={pillClass} style={{ borderColor: enconOrange }}>
            <ClipboardList className={pillIconClass} style={{ color: enconOrange }} />
            <span className={pillTextClass} style={{ color: enconOrange }}>P.O. Expenses</span>
          </button>

          {/* Price Increases */}
          <button onClick={() => navigate('/price-increases')} className={pillClass} style={{ borderColor: enconOrange }}>
            <TrendingUp className={pillIconClass} style={{ color: enconOrange }} />
            <span className={pillTextClass} style={{ color: enconOrange }}>Price Increases</span>
          </button>

          {/* New Accounts */}
          <button onClick={() => setShowNewAccountsModal(true)} className={pillClass} style={{ borderColor: enconOrange }}>
            <UserPlus className={pillIconClass} style={{ color: enconOrange }} />
            <span className={pillTextClass} style={{ color: enconOrange }}>New Accounts</span>
          </button>

          {/* Tools */}
          {user?.can_access_tools && <ToolsDropdown user={user} />}
        </div>
      </div>

      {/* Modals */}
      {proformaModalMode !== null && (
        <Proforma.Modal isOpen={true} onClose={() => setProformaModalMode(null)} mode={proformaModalMode} user={user} />
      )}
      {quotesModalMode !== null && (
        <Quotes.Modal isOpen={true} onClose={() => setQuotesModalMode(null)} mode={quotesModalMode} user={user} />
      )}
      <StockRequestModal
        isOpen={showStockRequestModal}
        onClose={() => setShowStockRequestModal(false)}
      />
      {showNewAccountsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setShowNewAccountsModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: '95vw', height: '92vh' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-white" />
                <span className="font-bold text-white text-lg">New Accounts</span>
              </div>
              <button onClick={() => setShowNewAccountsModal(false)} className="text-white hover:bg-white/20 rounded-full p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              src="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl"
              className="flex-1 w-full border-0 rounded-b-xl"
              title="New Accounts Form"
              allow="clipboard-write"
            />
          </div>
        </div>
      )}
    </>
  );
}
